import {formatQuotaResetLabel, isNotNullOrUndefined} from './copilot-chat-entitlement'
import {TBB_QUOTA_TRIGGER} from './copilot-chat-helpers'
import {
  type CopilotChatEntitlementQuotas,
  CopilotPlan,
  type QuotaLimitData,
  UserLevelBudgetState,
} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'

interface TbbStateInput {
  quotas: CopilotChatEntitlementQuotas | undefined
  plan: CopilotPlan | null
  hasCEorCBAccess: boolean
}

export interface TbbState {
  overagesEnabled: boolean
  userLevelBudgetEnabled: boolean
  userLevelBudgetState: UserLevelBudgetState | null
  userLevelBudgetPercentRemaining: number | null
  sessionQuotaRemaining: number | undefined
  sessionQuotaExceeded: boolean
  sessionQuotaResetLabel: string
  weeklyQuotaRemaining: number | undefined
  weeklyQuotaExceeded: boolean
  weeklyQuotaResetLabel: string
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  tbbQuotaExceeded: boolean
  isIndividualPaidOrStudentPlan: boolean
  isCommercialPlan: boolean
}

function getUserLevelBudgetState(
  userLevelBudgetEnabled: boolean,
  piQuota: QuotaLimitData | undefined,
  overagesEnabled: boolean,
): UserLevelBudgetState | null {
  if (!userLevelBudgetEnabled || !piQuota) return null
  const unlimited = !!piQuota.unlimited
  const hasQuota = piQuota.hasQuota !== false
  // Order matters: usage-blocked and no-limit both have unlimited truthy.
  if (unlimited && !hasQuota) return UserLevelBudgetState.UsageBlocked
  if (unlimited) return UserLevelBudgetState.NoLimit
  if (piQuota.percentRemaining <= 0 && !overagesEnabled) return UserLevelBudgetState.BudgetReached
  if (piQuota.percentRemaining <= TBB_QUOTA_TRIGGER) return UserLevelBudgetState.BudgetProgress
  return null
}

function getTbbQuotaExceeded(
  plan: CopilotPlan | null,
  piQuota: QuotaLimitData | undefined,
  sessionQuotaExceeded: boolean,
  weeklyQuotaExceeded: boolean,
  overagesEnabled: boolean,
  overagesExceeded: boolean,
): boolean {
  if (!copilotFeatureFlags.copilotDotcomChatTbb) return false
  if (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise) {
    return !!piQuota && !piQuota.unlimited && piQuota.percentRemaining <= 0
  }
  const quotaExceedButOveragesNotAllowed = (sessionQuotaExceeded || weeklyQuotaExceeded) && !overagesEnabled
  const overagesQuotaExceeded = overagesEnabled && (sessionQuotaExceeded || weeklyQuotaExceeded) && overagesExceeded
  return quotaExceedButOveragesNotAllowed || overagesQuotaExceeded
}

export function getTbbEntitlement({quotas, plan, hasCEorCBAccess}: TbbStateInput): TbbState {
  const overagesEnabled =
    (copilotFeatureFlags.copilotDotcomChatTbb ? quotas?.overageQuota?.permitted : quotas?.overagesEnabled) ?? false

  const userLevelBudgetEnabled =
    copilotFeatureFlags.copilotDotcomChatTbb && !!quotas?.tokenBasedBillingEnabled && hasCEorCBAccess

  const piQuota = quotas?.premiumInteractionsQuota
  const userLevelBudgetState = getUserLevelBudgetState(userLevelBudgetEnabled, piQuota, overagesEnabled)
  const userLevelBudgetPercentRemaining = userLevelBudgetEnabled && piQuota ? piQuota.percentRemaining : null

  // TBB Quota Limits (CFI session/weekly intervals)
  const sessionQuotaRemaining = quotas?.sessionQuota?.percentRemaining
  const sessionQuotaExceeded =
    quotas?.sessionQuota?.percentRemaining !== undefined && quotas?.sessionQuota?.percentRemaining <= 0
  const sessionQuotaResetLabel = formatQuotaResetLabel(quotas?.sessionQuota?.resetAt)
  const weeklyQuotaRemaining = quotas?.weeklyQuota?.percentRemaining
  const weeklyQuotaExceeded = isNotNullOrUndefined(weeklyQuotaRemaining) && weeklyQuotaRemaining <= 0
  const weeklyQuotaResetLabel = formatQuotaResetLabel(quotas?.weeklyQuota?.resetAt)
  const overagesRemaining = quotas?.overageQuota?.percentRemaining
  const overagesExceeded = isNotNullOrUndefined(overagesRemaining) && overagesRemaining <= 0

  const isIndividualPaidOrStudentPlan =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualStudent ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax
  const isCommercialPlan = plan === CopilotPlan.Enterprise || plan === CopilotPlan.Business

  const tbbQuotaExceeded = getTbbQuotaExceeded(
    plan,
    piQuota,
    sessionQuotaExceeded,
    weeklyQuotaExceeded,
    overagesEnabled,
    overagesExceeded,
  )

  return {
    overagesEnabled,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    sessionQuotaRemaining,
    sessionQuotaExceeded,
    sessionQuotaResetLabel,
    weeklyQuotaRemaining,
    weeklyQuotaExceeded,
    weeklyQuotaResetLabel,
    overagesRemaining,
    overagesExceeded,
    tbbQuotaExceeded,
    isIndividualPaidOrStudentPlan,
    isCommercialPlan,
  }
}
